import { Button } from "@/components/core/buttons/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/core/card";
import Combobox from "@/components/core/forms/combobox";
import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { Textarea } from "@/components/core/forms/textarea";
import projectService from "@/features/dashboard/api/services/project-service";
import { salesContractService } from "@/features/dashboard/api/services/sales-contract-service";
import { CONTRACT_TYPE_OPTIONS, PROJECT_TYPE_OPTIONS, type SalesContractFormData, salesContractSchema } from "@/features/dashboard/types/sales-contract";
import { formatNumber } from "@/utils/format-number";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconCalendar, IconCheck, IconCurrencyPeso, IconEdit, IconFileInvoice, IconPlus, IconTrash, IconX } from "@tabler/icons-react";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import Alert from "../core/alert";
import ComboboxCreatable from "../core/forms/combox-creatable";

interface SalesContractFormProps {
	initialData?: Partial<SalesContractFormData>;
	projectData?: {
		id: string;
		name: string;
		category?: string;
		client?: {
			name: string;
			company: string | null;
		};
	};
	onSubmit: (data: SalesContractFormData) => void;
	isLoading?: boolean;
	existingInvoices?: Array<{
		id: string;
		sequence: number;
		amount: number;
		description: string;
		invoiceDate: string;
		dueDate: string;
		startDate: string;
		endDate: string;
		existingInvoiceId?: string;
		collection_status?: "pending" | "collected";
		date_collected?: string | null;
		invoice_number?: string;
	}>;
}

export const SalesContractForm: React.FC<SalesContractFormProps> = ({ initialData, projectData, onSubmit, isLoading = false, existingInvoices }) => {
	const [tagOptions, setTagOptions] = useState<{ value: string; label: string }[]>([]);

	useEffect(() => {
		salesContractService.searchTags("").then((tags) => setTagOptions(tags));
	}, []);
	const [selectedProjectCategory, setSelectedProjectCategory] = useState<string | null>(projectData?.category || null);
	const [contractCategory, setContractCategory] = useState<string | null>(initialData?.project_category || null);
	const [numberOfInvoices, setNumberOfInvoices] = useState<number>(initialData?.number_of_invoices || 1);
	const [selectedProjectData, setSelectedProjectData] = useState<Record<string, unknown> | null>(projectData || null);
	const [editingInvoiceId, setEditingInvoiceId] = useState<string | null>(null);
	const [modifiedInvoices, setModifiedInvoices] = useState<
		Record<
			string,
			{
				amount?: number;
				description?: string;
				invoiceDate?: string;
				dueDate?: string;
			}
		>
	>({});
	const [deletedInvoiceIds, setDeletedInvoiceIds] = useState<Set<string>>(new Set());
	const [addedInvoices, setAddedInvoices] = useState<
		Array<{
			id: string;
			sequence: number;
			amount: number;
			description: string;
			invoiceDate: string;
			dueDate: string;
			startDate: string;
			endDate: string;
		}>
	>([]);

	const form = useForm<SalesContractFormData>({
		resolver: zodResolver(salesContractSchema),
		defaultValues: {
			project_id: initialData?.project_id || "",
			contract_amount: initialData?.contract_amount ? Number(initialData.contract_amount) : 0,
			monthly_recurring_sales: initialData?.monthly_recurring_sales ? Number(initialData.monthly_recurring_sales) : 0,
			payment_type: initialData?.payment_type || null,
			project_type: initialData?.project_type || "revlv_project",
			contract_type: initialData?.contract_type || null,
			project_category: initialData?.project_category || null,
			royalty_amount: initialData?.royalty_amount ? Number(initialData.royalty_amount) : null,
			original_monthly_recurring_sales: initialData?.original_monthly_recurring_sales ? Number(initialData.original_monthly_recurring_sales) : 0,
			original_contract_amount: initialData?.original_contract_amount ? Number(initialData.original_contract_amount) : 0,
			number_of_invoices: initialData?.number_of_invoices || 1,
			start_date: initialData?.start_date || new Date().toISOString().split("T")[0],
			end_date: initialData?.end_date || "",
			notes: initialData?.notes || "",
			tags: initialData?.tags || [],
			is_vatable: initialData?.is_vatable !== undefined ? initialData?.is_vatable : true,
		},
	});

	// VAT calculation helper (12% VAT rate in Philippines)
	const VAT_RATE = 0.12;
	const calculateVATExclusive = (vatInclusiveAmount: number) => {
		// Formula: VAT Exclusive = VAT Inclusive / (1 + VAT Rate)
		return vatInclusiveAmount / (1 + VAT_RATE);
	};

	// Fetch project details when project_id changes
	const handleProjectChange = async (projectId: string) => {
		if (projectId) {
			try {
				const response = await projectService.findById(projectId);
				const project = response;
				setSelectedProjectCategory(project.category);
				setSelectedProjectData(project);

				// Set contract category based on project category
				if (project.category === "recurring-one-time") {
					// For recurring-one-time projects, default to recurring unless user has already set it
					if (!contractCategory) {
						setContractCategory("recurring");
						form.setValue("project_category", "recurring");
					}
				} else {
					// For other project types, set contract category to match project category
					setContractCategory(project.category);
					form.setValue("project_category", project.category);
				}

				// Clear recurring fields if one-time project is selected or if contract category is set to one-time
				if (project.category === "one-time" || (project.category === "recurring-one-time" && contractCategory === "one-time")) {
					form.setValue("monthly_recurring_sales", 0);
					form.setValue("payment_type", null);
				}
			} catch (error) {
				console.error("Failed to fetch project details:", error);
			}
		} else {
			setSelectedProjectCategory(null);
			setSelectedProjectData(null);
			setContractCategory(null);
		}
	};

	const isOneTimeProject = selectedProjectCategory === "one-time" || (selectedProjectCategory === "recurring-one-time" && contractCategory === "one-time");
	const isRecurringOneTimeProject = selectedProjectCategory === "recurring-one-time";

	// Calculate end date based on number of invoices and payment type
	const calculateEndDate = (startDate: string, numInvoices: number, paymentType: string | null) => {
		if (!startDate || !paymentType || numInvoices <= 0) {
			return "";
		}

		const start = new Date(startDate);
		let monthsToAdd = 0;

		switch (paymentType) {
			case "monthly":
				monthsToAdd = numInvoices;
				break;
			case "quarterly":
				monthsToAdd = numInvoices * 3;
				break;
			case "semi-annual":
				monthsToAdd = numInvoices * 6;
				break;
			case "annually":
				monthsToAdd = numInvoices * 12;
				break;
			default:
				monthsToAdd = numInvoices;
		}

		// Create end date by adding months
		const endDate = new Date(start);
		const startDay = endDate.getDate();

		// Add the months
		endDate.setMonth(endDate.getMonth() + monthsToAdd);

		// Handle month overflow (e.g., Jan 31 + 1 month should be Feb 28/29, not Mar 3)
		if (endDate.getDate() !== startDay) {
			endDate.setDate(0); // Go to last day of previous month
		}

		// Subtract 1 day to get the actual end date (one day before next period starts)
		endDate.setDate(endDate.getDate() - 1);

		return endDate.toISOString().split("T")[0];
	};

	// Calculate number of invoices based on start date, end date and payment type
	const calculateNumberOfInvoices = (startDate: string, endDate: string, paymentType: string | null) => {
		if (!startDate || !endDate || !paymentType) {
			return 1;
		}

		const start = new Date(startDate);
		const end = new Date(endDate);

		// Calculate the difference in months
		const yearDiff = end.getFullYear() - start.getFullYear();
		const monthDiff = end.getMonth() - start.getMonth();
		const totalMonths = yearDiff * 12 + monthDiff;

		// Add 1 to include the final period if end date is past the start of the final period
		const adjustedMonths = totalMonths + (end.getDate() >= start.getDate() ? 1 : 0);

		let invoicesPerMonth = 1;
		switch (paymentType) {
			case "monthly":
				invoicesPerMonth = 1;
				break;
			case "quarterly":
				invoicesPerMonth = 1 / 3;
				break;
			case "semi-annual":
				invoicesPerMonth = 1 / 6;
				break;
			case "annually":
				invoicesPerMonth = 1 / 12;
				break;
		}

		const calculatedInvoices = Math.round(adjustedMonths * invoicesPerMonth);
		return Math.max(1, calculatedInvoices); // Ensure at least 1 invoice
	};

	// Track the last source of change to prevent loops
	const [lastChangeSource, setLastChangeSource] = useState<"invoice_count" | "end_date" | null>(null);

	// Update end date when number of invoices, payment type, or start date changes
	useEffect(() => {
		if (lastChangeSource === "end_date") return; // Don't update if last change was from end date

		const startDate = form.watch("start_date");
		const paymentType = form.watch("payment_type");

		// Sync form field with local state
		form.setValue("number_of_invoices", numberOfInvoices);

		if (!isOneTimeProject && startDate && paymentType && numberOfInvoices > 0) {
			const calculatedEndDate = calculateEndDate(startDate, numberOfInvoices, paymentType);
			form.setValue("end_date", calculatedEndDate);
			setLastChangeSource("invoice_count");
			// Reset after a delay to allow manual changes
			setTimeout(() => setLastChangeSource(null), 500);
		}
	}, [numberOfInvoices, form.watch("payment_type"), form.watch("start_date"), isOneTimeProject]);

	// Update number of invoices when end date changes manually
	useEffect(() => {
		if (lastChangeSource === "invoice_count") return; // Don't update if last change was from invoice count

		const startDate = form.watch("start_date");
		const endDate = form.watch("end_date");
		const paymentType = form.watch("payment_type");

		if (!isOneTimeProject && startDate && endDate && paymentType) {
			const calculatedInvoices = calculateNumberOfInvoices(startDate, endDate, paymentType);
			if (calculatedInvoices !== numberOfInvoices) {
				setNumberOfInvoices(calculatedInvoices);
				form.setValue("number_of_invoices", calculatedInvoices);
				setLastChangeSource("end_date");
				// Reset after a delay to allow manual changes
				setTimeout(() => setLastChangeSource(null), 500);
			}
		}
	}, [form.watch("end_date"), form.watch("start_date"), form.watch("payment_type"), isOneTimeProject, numberOfInvoices]);

	// Handle initial project data loading
	useEffect(() => {
		const projectId = form.watch("project_id");
		if (projectId && !selectedProjectData) {
			handleProjectChange(projectId);
		}
	}, [form.watch("project_id"), selectedProjectData]);

	// Sync numberOfInvoices state with form value on initial load
	useEffect(() => {
		const formValue = form.watch("number_of_invoices");
		if (formValue && formValue !== numberOfInvoices) {
			setNumberOfInvoices(formValue);
		}
	}, [form.watch("number_of_invoices"), numberOfInvoices]);

	// Also sync on component mount if initialData exists
	useEffect(() => {
		if (initialData?.number_of_invoices && initialData.number_of_invoices !== numberOfInvoices) {
			setNumberOfInvoices(initialData.number_of_invoices);
		}
	}, [initialData?.number_of_invoices]);

	// Track original start date to detect changes
	const originalStartDateRef = useRef<string | null>(initialData?.start_date || null);
	const lastProcessedStartDateRef = useRef<string | null>(null);

	// Handle start date changes in edit mode - adjust invoice dates proportionally
	useEffect(() => {
		const startDate = form.watch("start_date");
		const endDate = form.watch("end_date");
		const paymentType = form.watch("payment_type");

		// Only adjust if we have existing invoices (edit mode) and essential form data
		if (existingInvoices && existingInvoices.length > 0 && startDate && endDate && !isOneTimeProject && paymentType) {
			// Only adjust if start date actually changed and hasn't been processed yet
			if (originalStartDateRef.current && originalStartDateRef.current !== startDate && lastProcessedStartDateRef.current !== startDate) {
				// Mark this start date as processed to prevent loops
				lastProcessedStartDateRef.current = startDate;

				// Calculate date shift
				const originalStart = new Date(originalStartDateRef.current);
				const newStart = new Date(startDate);
				const daysDiff = Math.floor((newStart.getTime() - originalStart.getTime()) / (1000 * 60 * 60 * 24));

				// Create new modified invoices with adjusted dates, preserving existing modifications
				const newModifications: typeof modifiedInvoices = { ...modifiedInvoices };

				for (const invoice of existingInvoices) {
					if (!deletedInvoiceIds.has(invoice.id)) {
						// Start from original invoice dates, not previously modified ones
						const baseInvoiceDate = modifiedInvoices[invoice.id]?.invoiceDate || invoice.invoiceDate;
						const baseDueDate = modifiedInvoices[invoice.id]?.dueDate || invoice.dueDate;

						// Adjust invoice date from the original
						const originalInvoiceDate = new Date(invoice.invoiceDate);
						const newInvoiceDate = new Date(originalInvoiceDate);
						newInvoiceDate.setDate(newInvoiceDate.getDate() + daysDiff);

						// Adjust due date from the original
						const originalDueDate = new Date(invoice.dueDate);
						const newDueDate = new Date(originalDueDate);
						newDueDate.setDate(newDueDate.getDate() + daysDiff);

						newModifications[invoice.id] = {
							...newModifications[invoice.id],
							invoiceDate: newInvoiceDate.toISOString().split("T")[0],
							dueDate: newDueDate.toISOString().split("T")[0],
						};
					}
				}

				setModifiedInvoices(newModifications);

				// Also recalculate number of invoices if in edit mode
				if (endDate && paymentType) {
					const newNumberOfInvoices = calculateNumberOfInvoices(startDate, endDate, paymentType);
					if (newNumberOfInvoices !== numberOfInvoices) {
						setNumberOfInvoices(newNumberOfInvoices);
						form.setValue("number_of_invoices", newNumberOfInvoices);
					}
				}
			}
		}
	}, [form.watch("start_date"), existingInvoices, isOneTimeProject, form.watch("payment_type"), form.watch("end_date"), deletedInvoiceIds, numberOfInvoices]);

	// Helper functions for invoice editing
	const updateInvoice = (invoiceId: string, field: string, value: string | number) => {
		setModifiedInvoices((prev) => ({
			...prev,
			[invoiceId]: {
				...prev[invoiceId],
				[field]: value,
			},
		}));
	};

	// Helper function to get invoice type and styling
	const getInvoiceTypeInfo = (invoice: Record<string, unknown>) => {
		if (invoice.existingInvoiceId) {
			return {
				type: "existing",
				label: "Existing",
				bgClass: "bg-green-100",
				textClass: "text-green-800",
			};
		}
		if (invoice.id.startsWith("added-")) {
			return {
				type: "added",
				label: "Added",
				bgClass: "bg-orange-100",
				textClass: "text-orange-800",
			};
		}
		return {
			type: "preview",
			label: "New",
			bgClass: "bg-blue-100",
			textClass: "text-blue-800",
		};
	};

	const getInvoiceValue = (invoiceId: string, field: string, defaultValue: string | number) => {
		return modifiedInvoices[invoiceId]?.[field] ?? defaultValue;
	};

	const saveInvoiceEdit = (invoiceId: string) => {
		const modifications = modifiedInvoices[invoiceId];
		if (modifications) {
			// Basic validation
			if (modifications.amount !== undefined && modifications.amount <= 0) {
				alert("Invoice amount must be greater than 0");
				return;
			}

			if (modifications.invoiceDate && modifications.dueDate) {
				const invoiceDate = new Date(modifications.invoiceDate);
				const dueDate = new Date(modifications.dueDate);
				if (dueDate <= invoiceDate) {
					alert("Due date must be after invoice date");
					return;
				}
			}

			if (modifications.description !== undefined && modifications.description.trim().length === 0) {
				alert("Invoice description cannot be empty");
				return;
			}
		}

		setEditingInvoiceId(null);
	};

	const cancelInvoiceEdit = (invoiceId: string) => {
		setModifiedInvoices((prev) => {
			const updated = { ...prev };
			delete updated[invoiceId];
			return updated;
		});
		setEditingInvoiceId(null);
	};

	// Helper function to generate form-based invoices (extracted from original logic)
	const generateFormBasedInvoices = () => {
		const startDate = form.watch("start_date");
		const paymentType = form.watch("payment_type");
		const monthlyRecurring = form.watch("monthly_recurring_sales");
		const contractAmount = form.watch("contract_amount");
		const projectType = form.watch("project_type");
		const royaltyAmount = form.watch("royalty_amount");
		const isRoyalty = projectType === "royalty_project" && royaltyAmount && royaltyAmount > 0;
		const effectiveOneTimeAmount = isRoyalty ? royaltyAmount : contractAmount;

		if (!startDate || isOneTimeProject) {
			// For one-time projects, show single invoice
			if (isOneTimeProject && (contractAmount > 0 || (isRoyalty && royaltyAmount > 0))) {
				const invoiceId = "one-time-1";
				return [
					{
						id: invoiceId,
						sequence: 1,
						amount: getInvoiceValue(invoiceId, "amount", effectiveOneTimeAmount),
						invoiceDate: getInvoiceValue(invoiceId, "invoiceDate", startDate || new Date().toISOString().split("T")[0]),
						dueDate: getInvoiceValue(
							invoiceId,
							"dueDate",
							(() => {
								const date = new Date(startDate || new Date());
								date.setMonth(date.getMonth() + 1);
								// Handle month overflow
								if (date.getDate() !== new Date(startDate || new Date()).getDate()) {
									date.setDate(0);
								}
								return date.toISOString().split("T")[0];
							})(),
						),
						startDate: startDate || new Date().toISOString().split("T")[0],
						endDate: startDate || new Date().toISOString().split("T")[0],
						description: getInvoiceValue(invoiceId, "description", "Project Invoice"),
					},
				];
			}
			return [];
		}

		if (!paymentType || numberOfInvoices <= 0 || monthlyRecurring <= 0) {
			return [];
		}

		const invoices = [];
		const start = new Date(startDate);
		let currentPeriodStart = new Date(start);

		for (let i = 0; i < numberOfInvoices; i++) {
			const periodEnd = new Date(currentPeriodStart);
			let monthsToAdd = 0;

			switch (paymentType) {
				case "monthly":
					monthsToAdd = 1;
					break;
				case "quarterly":
					monthsToAdd = 3;
					break;
				case "semi-annual":
					monthsToAdd = 6;
					break;
				case "annually":
					monthsToAdd = 12;
					break;
				default:
					monthsToAdd = 1;
			}

			// Calculate period end
			periodEnd.setMonth(periodEnd.getMonth() + monthsToAdd);

			// Handle month overflow
			if (periodEnd.getDate() !== currentPeriodStart.getDate()) {
				periodEnd.setDate(0);
			}

			// Subtract 1 day to get the actual end date of the period
			periodEnd.setDate(periodEnd.getDate() - 1);

			// Calculate due date (1 month after invoice date)
			const dueDate = new Date(currentPeriodStart);
			dueDate.setMonth(dueDate.getMonth() + 1);
			if (dueDate.getDate() !== currentPeriodStart.getDate()) {
				dueDate.setDate(0);
			}

			// Calculate amount based on payment type — use royalty_amount for royalty projects
			const baseAmount = isRoyalty ? royaltyAmount : monthlyRecurring;
			let amount = baseAmount;
			if (paymentType === "quarterly") {
				amount = baseAmount * 3;
			} else if (paymentType === "semi-annual") {
				amount = baseAmount * 6;
			} else if (paymentType === "annually") {
				amount = baseAmount * 12;
			}

			const invoiceId = `recurring-${i + 1}`;
			const periodName =
				paymentType === "monthly" ? "Monthly" : paymentType === "quarterly" ? "Quarterly" : paymentType === "semi-annual" ? "Semi-Annual" : "Annual";

			invoices.push({
				id: invoiceId,
				sequence: i + 1,
				amount: getInvoiceValue(invoiceId, "amount", amount),
				invoiceDate: getInvoiceValue(invoiceId, "invoiceDate", currentPeriodStart.toISOString().split("T")[0]),
				dueDate: getInvoiceValue(invoiceId, "dueDate", dueDate.toISOString().split("T")[0]),
				startDate: currentPeriodStart.toISOString().split("T")[0],
				endDate: periodEnd.toISOString().split("T")[0],
				description: getInvoiceValue(invoiceId, "description", `${periodName} Service Invoice`),
			});

			// Move to next period
			currentPeriodStart = new Date(periodEnd);
			currentPeriodStart.setDate(currentPeriodStart.getDate() + 1);
		}

		return invoices;
	};

	// Function to add a new invoice
	const addNewInvoice = () => {
		// Calculate next sequence from all visible invoices (excluding deleted ones)
		const visibleInvoices = [...(existingInvoices || []), ...addedInvoices].filter((inv) => !deletedInvoiceIds.has(inv.id));
		const nextSequence = Math.max(...visibleInvoices.map((inv) => inv.sequence), 0) + 1;

		// Calculate default dates
		const today = new Date();
		const defaultInvoiceDate = today.toISOString().split("T")[0];
		const dueDate = new Date(today);
		dueDate.setMonth(dueDate.getMonth() + 1);
		const defaultDueDate = dueDate.toISOString().split("T")[0];

		// Calculate default amount based on project type and payment type
		const paymentType = form.watch("payment_type");
		const monthlyRecurring = form.watch("monthly_recurring_sales");
		const contractAmount = form.watch("contract_amount");
		const projectType = form.watch("project_type");
		const royaltyAmount = form.watch("royalty_amount");
		const isRoyaltyProject = projectType === "royalty_project" && royaltyAmount && royaltyAmount > 0;
		const baseRecurring = isRoyaltyProject ? royaltyAmount : monthlyRecurring || 0;

		let defaultAmount = 0;
		if (isOneTimeProject) {
			defaultAmount = isRoyaltyProject ? royaltyAmount : contractAmount || 0;
		} else {
			switch (paymentType) {
				case "monthly":
					defaultAmount = baseRecurring;
					break;
				case "quarterly":
					defaultAmount = baseRecurring * 3;
					break;
				case "semi-annual":
					defaultAmount = baseRecurring * 6;
					break;
				case "annually":
					defaultAmount = baseRecurring * 12;
					break;
				default:
					defaultAmount = baseRecurring;
			}
		}

		const newInvoice = {
			id: `added-${Date.now()}`,
			sequence: nextSequence,
			amount: defaultAmount,
			description: isOneTimeProject ? "Project Invoice" : `${paymentType || "Monthly"} Service Invoice`,
			invoiceDate: defaultInvoiceDate,
			dueDate: defaultDueDate,
			startDate: defaultInvoiceDate,
			endDate: defaultInvoiceDate,
		};

		setAddedInvoices((prev) => [...prev, newInvoice]);
	};

	// Function to remove an invoice
	const removeInvoice = (invoiceId: string) => {
		// If it's an existing invoice, mark as deleted
		if (invoiceId.startsWith("existing-") || invoiceId.startsWith("recurring-") || invoiceId.startsWith("one-time-")) {
			setDeletedInvoiceIds((prev) => new Set([...prev, invoiceId]));
		} else if (invoiceId.startsWith("added-")) {
			// If it's a newly added invoice, remove from added list
			setAddedInvoices((prev) => prev.filter((inv) => inv.id !== invoiceId));
		}

		// Remove any modifications for this invoice
		setModifiedInvoices((prev) => {
			const updated = { ...prev };
			delete updated[invoiceId];
			return updated;
		});

		// Stop editing if currently editing this invoice
		if (editingInvoiceId === invoiceId) {
			setEditingInvoiceId(null);
		}
	};

	// Calculate effective deleted IDs (includes automatic deletions from count reduction)
	const effectiveDeletedIds = useMemo(() => {
		const deletedIds = new Set(deletedInvoiceIds);
		const targetCount = numberOfInvoices;

		// Don't auto-delete for one-time projects
		if (isOneTimeProject) {
			return deletedIds;
		}

		// Get all current invoices (existing + added) that are not manually deleted
		let allCurrentInvoices = [];

		if (existingInvoices && existingInvoices.length > 0) {
			// Edit mode: include existing invoices
			allCurrentInvoices = [...existingInvoices, ...addedInvoices];
		} else {
			// Create mode: generate form-based invoices to get the current set
			const formInvoices = generateFormBasedInvoices();
			allCurrentInvoices = [...formInvoices, ...addedInvoices];
		}

		// Filter out manually deleted invoices
		const currentVisibleInvoices = allCurrentInvoices.filter((inv) => !deletedIds.has(inv.id));

		// If we have more invoices than the target count, mark excess as deleted
		if (targetCount < currentVisibleInvoices.length) {
			// Sort by sequence to delete from the end
			const sortedInvoices = currentVisibleInvoices.sort((a, b) => a.sequence - b.sequence);
			const excessInvoices = sortedInvoices.slice(targetCount);

			for (const inv of excessInvoices) {
				deletedIds.add(inv.id);
			}
		}

		return deletedIds;
	}, [
		deletedInvoiceIds,
		numberOfInvoices,
		existingInvoices,
		addedInvoices,
		isOneTimeProject,
		form.watch("start_date"),
		form.watch("payment_type"),
		form.watch("monthly_recurring_sales"),
		form.watch("contract_amount"),
	]);

	// Update the deletedInvoiceIds state when effectiveDeletedIds changes
	useEffect(() => {
		if (effectiveDeletedIds.size !== deletedInvoiceIds.size || ![...effectiveDeletedIds].every((id) => deletedInvoiceIds.has(id))) {
			setDeletedInvoiceIds(effectiveDeletedIds);
		}
	}, [effectiveDeletedIds, deletedInvoiceIds]);

	// Generate preview of invoices that will be created
	const invoicePreview = useMemo(() => {
		let baseInvoices = [];

		// If we have existing invoices (edit mode), use them as base
		if (existingInvoices && existingInvoices.length > 0) {
			baseInvoices = existingInvoices.map((invoice) => ({
				...invoice,
				amount: getInvoiceValue(invoice.id, "amount", invoice.amount),
				description: getInvoiceValue(invoice.id, "description", invoice.description),
				invoiceDate: getInvoiceValue(invoice.id, "invoiceDate", invoice.invoiceDate),
				dueDate: getInvoiceValue(invoice.id, "dueDate", invoice.dueDate),
				// Preserve collection metadata
				collection_status: invoice.collection_status,
				date_collected: invoice.date_collected,
				invoice_number: invoice.invoice_number,
				existingInvoiceId: invoice.existingInvoiceId,
			}));

			// Handle numberOfInvoices changes in edit mode
			const targetCount = numberOfInvoices;
			const existingVisibleCount = baseInvoices.filter((inv) => !deletedInvoiceIds.has(inv.id)).length;

			if (targetCount > existingVisibleCount && !isOneTimeProject) {
				// Generate only the additional invoices needed
				const additionalInvoicesNeeded = targetCount - existingVisibleCount;
				const currentMaxSequence = Math.max(...baseInvoices.map((inv) => inv.sequence), 0);

				// Generate invoices starting from the next sequence
				const startDate = form.watch("start_date");
				const paymentType = form.watch("payment_type");
				const monthlyRecurring = form.watch("monthly_recurring_sales");

				if (startDate && paymentType && monthlyRecurring > 0) {
					const additionalInvoices = [];
					const start = new Date(startDate);

					// Calculate starting period for additional invoices (after existing ones)
					let currentPeriodStart = new Date(start);
					let monthsToAdd = 0;
					switch (paymentType) {
						case "monthly":
							monthsToAdd = 1;
							break;
						case "quarterly":
							monthsToAdd = 3;
							break;
						case "semi-annual":
							monthsToAdd = 6;
							break;
						case "annually":
							monthsToAdd = 12;
							break;
						default:
							monthsToAdd = 1;
					}

					// Skip to the period after existing invoices
					for (let i = 0; i < existingVisibleCount; i++) {
						currentPeriodStart.setMonth(currentPeriodStart.getMonth() + monthsToAdd);
						if (currentPeriodStart.getDate() !== start.getDate()) {
							currentPeriodStart.setDate(0);
						}
					}

					// Generate additional invoices
					for (let i = 0; i < additionalInvoicesNeeded; i++) {
						const periodEnd = new Date(currentPeriodStart);
						periodEnd.setMonth(periodEnd.getMonth() + monthsToAdd);
						if (periodEnd.getDate() !== currentPeriodStart.getDate()) {
							periodEnd.setDate(0);
						}
						periodEnd.setDate(periodEnd.getDate() - 1);

						const dueDate = new Date(currentPeriodStart);
						dueDate.setMonth(dueDate.getMonth() + 1);
						if (dueDate.getDate() !== currentPeriodStart.getDate()) {
							dueDate.setDate(0);
						}

						let amount = monthlyRecurring;
						if (paymentType === "quarterly") amount = monthlyRecurring * 3;
						else if (paymentType === "semi-annual") amount = monthlyRecurring * 6;
						else if (paymentType === "annually") amount = monthlyRecurring * 12;

						const invoiceId = `generated-${currentMaxSequence + i + 1}`;
						const periodName =
							paymentType === "monthly" ? "Monthly" : paymentType === "quarterly" ? "Quarterly" : paymentType === "semi-annual" ? "Semi-Annual" : "Annual";

						additionalInvoices.push({
							id: invoiceId,
							sequence: currentMaxSequence + i + 1,
							amount: amount,
							invoiceDate: currentPeriodStart.toISOString().split("T")[0],
							dueDate: dueDate.toISOString().split("T")[0],
							startDate: currentPeriodStart.toISOString().split("T")[0],
							endDate: periodEnd.toISOString().split("T")[0],
							description: `${periodName} Service Invoice`,
						});

						// Move to next period
						currentPeriodStart = new Date(periodEnd);
						currentPeriodStart.setDate(currentPeriodStart.getDate() + 1);
					}

					baseInvoices = [...baseInvoices, ...additionalInvoices];
				}
			}
		} else {
			// Generate preview based on form data (create mode)
			baseInvoices = generateFormBasedInvoices();
		}

		// Filter out deleted invoices and add newly added invoices
		const filteredInvoices = baseInvoices.filter((invoice) => !effectiveDeletedIds.has(invoice.id));
		const allInvoices = [...filteredInvoices, ...addedInvoices];

		// Sort by invoice date first, then re-sequence to ensure proper order
		const sortedInvoices = allInvoices.sort((a, b) => {
			const dateA = new Date(a.invoiceDate).getTime();
			const dateB = new Date(b.invoiceDate).getTime();
			// If dates are equal, sort by original sequence to maintain stability
			return dateA !== dateB ? dateA - dateB : a.sequence - b.sequence;
		});

		// Re-sequence the invoices to prevent duplicates
		return sortedInvoices.map((invoice, index) => ({
			...invoice,
			sequence: index + 1,
		}));
	}, [
		existingInvoices,
		effectiveDeletedIds,
		addedInvoices,
		form.watch("start_date"),
		form.watch("payment_type"),
		form.watch("monthly_recurring_sales"),
		form.watch("contract_amount"),
		numberOfInvoices,
		isOneTimeProject,
		modifiedInvoices,
	]);

	// Calculate total invoice amount and check if it exceeds contract amount
	const totalInvoiceAmount = useMemo(() => {
		return invoicePreview.reduce((total, invoice) => total + getInvoiceValue(invoice.id, "amount", invoice.amount), 0);
	}, [invoicePreview, modifiedInvoices]);

	const contractAmount = form.watch("contract_amount") || 0;
	const isInvoiceTotalExceeded = totalInvoiceAmount > contractAmount;

	return (
		<div className="min-h-screen bg-gray-50">
			<Form {...form}>
				<form
					id="sales-contract-form"
					className="h-screen flex"
					onSubmit={form.handleSubmit((data) => {
						// Apply modifications to invoice preview data before submission
						const finalInvoicePreview = invoicePreview.map((invoice) => ({
							...invoice,
							amount: getInvoiceValue(invoice.id, "amount", invoice.amount),
							description: getInvoiceValue(invoice.id, "description", invoice.description),
							invoiceDate: getInvoiceValue(invoice.id, "invoiceDate", invoice.invoiceDate),
							dueDate: getInvoiceValue(invoice.id, "dueDate", invoice.dueDate),
							// Preserve collection metadata for existing invoices
							collection_status: invoice.collection_status,
							date_collected: invoice.date_collected,
							invoice_number: invoice.invoice_number,
							existingInvoiceId: invoice.existingInvoiceId,
						}));

						// For Revlv projects, set original amounts to current amounts if not already set
						const processedData = { ...data };
						if (data.project_type === "revlv_project") {
							if (!data.original_contract_amount || data.original_contract_amount === 0) {
								processedData.original_contract_amount = data.contract_amount;
							}
							if (!data.original_monthly_recurring_sales || data.original_monthly_recurring_sales === 0) {
								processedData.original_monthly_recurring_sales = data.monthly_recurring_sales;
							}
						}

						// Include all invoice-related data in submission
						const formDataWithInvoices = {
							...processedData,
							invoice_modifications: modifiedInvoices,
							invoice_preview: finalInvoicePreview,
							deleted_invoice_ids: Array.from(effectiveDeletedIds),
							added_invoices: addedInvoices,
							// Update number of invoices to reflect actual count
							number_of_invoices: invoicePreview.length,
						};
						onSubmit(formDataWithInvoices);
					})}
				>
					{/* Left Side - Contract Details */}
					<div className="w-1/2 bg-white shadow-lg overflow-y-auto">
						<div className="p-8">
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-gray-900 mb-2">{initialData ? "Edit Sales Contract" : "Create Sales Contract"}</h2>
								<p className="text-gray-600">Configure contract details and payment terms</p>
							</div>

							<div className="space-y-6">
								{/* Project Selection */}
								<div className="space-y-4">
									<FormField
										control={form.control}
										name="project_id"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>Project</FormLabel>
												{selectedProjectCategory && (
													<div className="mb-2">
														<span
															className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
																selectedProjectCategory === "one-time"
																	? "bg-blue-100 text-blue-800"
																	: selectedProjectCategory === "recurring"
																		? "bg-green-100 text-green-800"
																		: selectedProjectCategory === "recurring-one-time"
																			? "bg-purple-100 text-purple-800"
																			: "bg-gray-100 text-gray-800"
															}`}
														>
															{selectedProjectCategory === "one-time"
																? "One-time Project"
																: selectedProjectCategory === "recurring"
																	? "Recurring Project"
																	: selectedProjectCategory === "recurring-one-time"
																		? "Recurring & One-time Project"
																		: "Unknown Project"}
														</span>
													</div>
												)}
												<FormGroup>
													<Combobox
														value={field.value}
														placeholderValue={
															selectedProjectData
																? {
																		value: selectedProjectData.id,
																		label: `${selectedProjectData.name} ${selectedProjectData.client ? `(${selectedProjectData.client.company || selectedProjectData.client.name})` : ""}`,
																	}
																: undefined
														}
														onValueChange={(value) => {
															field.onChange(value || "");
															handleProjectChange(value || "");
														}}
														loadOptions={async (value: string) => {
															const response = await projectService.search(value);
															return response.data.map((project) => ({
																label: `${project.name} ${project.client ? `(${project.client.company || project.client.name})` : ""} - ${
																	project.category === "one-time"
																		? "One-time"
																		: project.category === "recurring"
																			? "Recurring"
																			: project.category === "recurring-one-time"
																				? "Recurring & One-time"
																				: "Unknown"
																}`,
																value: project.id,
															}));
														}}
														placeholder="Select a project..."
													/>
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</div>

							{/* Contract Category Selection for Recurring & One-time Projects */}
							{isRecurringOneTimeProject && (
								<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
									<div className="col-span-12">
										<FormField
											control={form.control}
											name="project_category"
											render={({ field }) => (
												<FormItem>
													<FormLabel required>Contract Category</FormLabel>
													<FormGroup>
														<Select
															placeholder="Select contract category"
															options={[
																{ value: "recurring", label: "Recurring" },
																{ value: "one-time", label: "One-time" },
															]}
															defaultValue={contractCategory || "recurring"}
															onValueChange={(value) => {
																setContractCategory(value);
																field.onChange(value);
																// Clear recurring fields if switching to one-time
																if (value === "one-time") {
																	form.setValue("monthly_recurring_sales", 0);
																	form.setValue("payment_type", null);
																}
															}}
														/>
													</FormGroup>
													<div className="text-xs text-gray-500 mt-1">Specify how this contract should be treated for this "Recurring & One-time" project.</div>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								</div>
							)}

							<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
								<div className="col-span-4">
									<FormField
										control={form.control}
										name="project_type"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>Project Type</FormLabel>
												<FormGroup>
													<Select placeholder="Select project type" options={PROJECT_TYPE_OPTIONS} defaultValue={field.value} onValueChange={field.onChange} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="col-span-4">
									<FormField
										control={form.control}
										name="contract_type"
										render={({ field }) => (
											<FormItem>
												<FormLabel>Contract Type</FormLabel>
												<FormGroup>
													<Select
														placeholder="Select contract type"
														options={[{ value: "none", label: "None" }, ...CONTRACT_TYPE_OPTIONS]}
														defaultValue={field.value || "none"}
														onValueChange={(value) => field.onChange(value === "none" ? null : value)}
													/>
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="col-span-4">
									<FormField
										control={form.control}
										name="is_vatable"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>VAT Status</FormLabel>
												<FormGroup>
													<Select
														placeholder="Select VAT status"
														options={[
															{ value: "true", label: "VATable" },
															{ value: "false", label: "Non-VATable" },
														]}
														defaultValue={field.value !== undefined ? (field.value ? "true" : "false") : "true"}
														onValueChange={(value) => field.onChange(value === "true")}
													/>
												</FormGroup>
												<div className="text-xs text-gray-500 mt-1">Specify if this contract is subject to VAT</div>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</div>

							{form.watch("project_type") === "royalty_project" && (
								<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
									<Alert type="info" className="col-span-12">
										<div className="flex items-center">
											<IconCurrencyPeso className="mr-2 h-5 w-5 flex-shrink-0" />
											<div>
												<p className="font-medium">Royalty Project Selected</p>
												<p className="text-sm text-gray-700">Royalty projects typically involve payments based on a percentage of revenue or profits.</p>
												<p className="text-sm text-gray-700">
													Enter the <strong>Original contract amount</strong> and <strong>Orignal monthly recurring sales</strong>.
												</p>
												<p className="text-sm text-gray-700">
													Enter the current <strong>Contract amount</strong> and <strong>Monthly recurring sales</strong> to reflect any royalty amount.
												</p>
											</div>
										</div>
									</Alert>
								</div>
							)}

							{form.watch("is_vatable") === false && (
								<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
									<Alert type="info" className="col-span-12">
										<div className="flex items-center">
											<IconFileInvoice className="mr-2 h-5 w-5 flex-shrink-0" />
											<div>
												<p className="font-medium">Non-VATable Contract</p>
												<p className="text-sm text-gray-700">This contract is not subject to VAT.</p>
												<p className="text-sm text-gray-700">
													Enter the <strong>Original contract amount</strong> (VAT inclusive amount).
												</p>
												<p className="text-sm text-gray-700">
													The current contract amount will be <strong>automatically calculated</strong> as VAT-exclusive value (using 12% VAT rate).
												</p>
												<p className="text-xs text-gray-600 mt-1">Formula: Contract Amount (VAT Exclusive) = Original Contract Amount ÷ 1.12</p>
											</div>
										</div>
									</Alert>
								</div>
							)}

							{(form.watch("project_type") === "royalty_project" || form.watch("is_vatable") === false) && (
								<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
									<div className={isOneTimeProject ? "col-span-12" : "col-span-6"}>
										<FormField
											control={form.control}
											name="original_contract_amount"
											render={({ field }) => (
												<FormItem>
													<FormLabel required>
														{isOneTimeProject ? "Original Project Amount" : "Original Contract Amount"}
														{form.watch("is_vatable") === false && " (VAT Inclusive)"}
													</FormLabel>
													<FormGroup>
														<Input
															className="input"
															type="number"
															step="0.01"
															placeholder="0.00"
															name={field.name}
															value={field.value || ""}
															onBlur={field.onBlur}
															onChange={(e) => {
																const originalAmount = e.target.value ? Number(e.target.value) : 0;
																field.onChange(originalAmount);

																// Auto-calculate VAT-exclusive amount if Non-VATable is selected
																if (form.watch("is_vatable") === false && originalAmount > 0) {
																	const vatExclusiveAmount = calculateVATExclusive(originalAmount);
																	form.setValue("contract_amount", Number(vatExclusiveAmount.toFixed(2)));
																}
															}}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									{!isOneTimeProject && form.watch("project_type") === "royalty_project" && (
										<div className="col-span-6">
											<FormField
												control={form.control}
												name="original_monthly_recurring_sales"
												render={({ field }) => (
													<FormItem>
														<FormLabel required>Original Monthly Recurring Sales (VAT Inclusive)</FormLabel>
														<FormGroup>
															<Input
																className="input"
																type="number"
																step="0.01"
																placeholder="0.00"
																name={field.name}
																value={field.value || ""}
																onBlur={field.onBlur}
																onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 0)}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
									)}
								</div>
							)}

							{form.watch("project_type") === "royalty_project" && (
								<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
									<div className="col-span-6">
										<FormField
											control={form.control}
											name="royalty_amount"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Royalty Amount</FormLabel>
													<FormGroup>
														<Input
															className="input"
															type="number"
															step="0.01"
															placeholder="0.00"
															name={field.name}
															value={field.value || ""}
															onBlur={field.onBlur}
															onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								</div>
							)}

							<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
								{form.watch("project_type") !== "royalty_project" && (
									<div className={isOneTimeProject ? "col-span-12" : "col-span-6"}>
										<FormField
											control={form.control}
											name="contract_amount"
											render={({ field }) => (
												<FormItem>
													<FormLabel required>
														{isOneTimeProject ? "Project Amount" : "Contract Amount"}
														{form.watch("is_vatable") === false && " (VAT Exclusive)"}
													</FormLabel>
													<FormGroup>
														<Input
															className="input"
															type="number"
															step="0.01"
															placeholder="0.00"
															name={field.name}
															value={field.value || ""}
															onBlur={field.onBlur}
															onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 0)}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								)}
								{!isOneTimeProject && (
									<div className={form.watch("project_type") === "royalty_project" ? "col-span-12" : "col-span-6"}>
										<FormField
											control={form.control}
											name="monthly_recurring_sales"
											render={({ field }) => (
												<FormItem>
													<FormLabel required>Monthly Recurring Sales (VAT Inclusive)</FormLabel>
													<FormGroup>
														<Input
															className="input"
															type="number"
															step="0.01"
															placeholder="0.00"
															name={field.name}
															value={field.value || ""}
															onBlur={field.onBlur}
															onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 0)}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								)}
							</div>

							{!isOneTimeProject && (
								<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
									<div className="col-span-6">
										<FormField
											control={form.control}
											name="payment_type"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Payment Type</FormLabel>
													<FormGroup>
														<Select
															placeholder="Select payment type"
															options={[
																{ value: "monthly", label: "Monthly" },
																{ value: "quarterly", label: "Quarterly" },
																{ value: "semi-annual", label: "Semi Annual" },
																{ value: "annually", label: "Annually" },
															]}
															defaultValue={field.value}
															onValueChange={field.onChange}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-6">
										<FormField
											control={form.control}
											name="number_of_invoices"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Number of Invoices</FormLabel>
													<FormGroup>
														<Input
															className="input"
															type="number"
															min="1"
															placeholder="1"
															name={field.name}
															value={numberOfInvoices}
															onBlur={field.onBlur}
															onChange={(e) => {
																const value = Number.parseInt(e.target.value) || 1;
																const finalValue = value > 0 ? value : 1;
																setNumberOfInvoices(finalValue);
																field.onChange(finalValue);
															}}
														/>
													</FormGroup>
													<div className="text-xs text-gray-500 mt-1">
														{existingInvoices && existingInvoices.length > 0
															? `Currently ${existingInvoices.length} existing invoices - edit to change total count`
															: "Auto-calculates end date based on payment type"}
													</div>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								</div>
							)}

							<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
								<div className="col-span-6">
									<FormField
										control={form.control}
										name="start_date"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>Start Date</FormLabel>
												<FormGroup>
													<Input className="input" type="date" {...field} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="col-span-6">
									<FormField
										control={form.control}
										name="end_date"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>End Date</FormLabel>
												<FormGroup>
													<Input
														className="input"
														type="date"
														{...field}
														onChange={(e) => {
															field.onChange(e);
															// Mark this change as coming from end date
															setLastChangeSource("end_date");
														}}
														title={
															!isOneTimeProject && form.watch("payment_type") !== null
																? "Auto-calculated based on number of invoices, or adjust to change invoice count"
																: ""
														}
													/>
												</FormGroup>
												{!isOneTimeProject && form.watch("payment_type") && (
													<div className="text-xs text-gray-500 mt-1">
														Auto-calculated for {numberOfInvoices} invoice{numberOfInvoices > 1 ? "s" : ""} - adjust to change invoice count
													</div>
												)}
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</div>

							{/* Notes Section */}
							<div className="space-y-4">
								<FormField
									control={form.control}
									name="notes"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Notes</FormLabel>
											<FormGroup>
												<Textarea className="input" placeholder="Enter any additional notes..." rows={4} {...field} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							{/* Tags Section */}
							<div className="space-y-4">
								<FormField
									control={form.control}
									name="tags"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Tags</FormLabel>
											<ComboboxCreatable
												isMulti
												placeholder="Type to search or create tags..."
												loadOptions={(inputValue: string) => salesContractService.searchTags(inputValue)}
												options={tagOptions}
												onValueChange={(selected) => {
													field.onChange(selected.map((s) => s.label));
												}}
												controlledValue={(field.value || []).map((tag: string) => ({ value: tag, label: tag }))}
											/>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>

						{/* Submit Button */}
						<div className="p-8 border-t bg-gray-50">
							<div className="flex flex-col gap-2">
								<div className="flex justify-start gap-2">
									<Button type="submit" disabled={isLoading || isInvoiceTotalExceeded} className="px-8">
										{isLoading ? "Saving..." : initialData ? "Update Contract" : "Create Contract"}
									</Button>
								</div>
								{isInvoiceTotalExceeded && <div className="text-sm text-red-600">Cannot save: Total invoice amount exceeds contract amount</div>}
							</div>
						</div>
					</div>
					{/* </div> */}

					{/* Right Side - Invoice Management */}
					<div className="w-1/2 bg-gray-100 border-l overflow-y-auto">
						<div className="p-8">
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
									<IconFileInvoice className="w-6 h-6 text-blue-600" />
									Invoice Management
								</h2>
								<p className="text-gray-600">
									{existingInvoices && existingInvoices.length > 0
										? "Edit existing invoices, add new ones, or delete as needed"
										: "Preview and customize invoices that will be generated"}
								</p>
							</div>

							{/* Invoice Preview Section */}
							{invoicePreview.length > 0 ? (
								<div className="space-y-6">
									{/* Header with Add Button */}
									<div className="flex items-center justify-between">
										<div className="flex items-center gap-2">
											<span className="text-lg font-semibold text-gray-900">
												{existingInvoices && existingInvoices.length > 0 ? "Existing Invoices" : "Invoice Preview"}
											</span>
											<span className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded-full">
												{invoicePreview.length} invoice{invoicePreview.length > 1 ? "s" : ""}
											</span>
										</div>
										<Button type="button" size="sm" variant="outline" onClick={addNewInvoice} className="text-green-600 border-green-300 hover:bg-green-50">
											<IconPlus className="w-4 h-4 mr-1" />
											Add Invoice
										</Button>
									</div>

									{/* Invoice List */}

									{/* Total Amount */}
									<div className={`p-4 rounded-lg border ${isInvoiceTotalExceeded ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
										<div className="flex justify-between items-center">
											<span className={`text-lg font-semibold ${isInvoiceTotalExceeded ? "text-red-800" : "text-green-800"}`}>Total Invoice Amount:</span>
											<div className={`flex items-center gap-2 text-2xl font-bold ${isInvoiceTotalExceeded ? "text-red-800" : "text-green-800"}`}>
												<IconCurrencyPeso className="w-6 h-6" />
												{formatNumber(totalInvoiceAmount)}
											</div>
										</div>
										{isInvoiceTotalExceeded && (
											<div className="mt-2 text-sm text-red-700">
												⚠️ Total invoice amount ({formatNumber(totalInvoiceAmount)}) exceeds contract amount ({formatNumber(contractAmount)}). Please adjust
												invoice amounts or increase the contract amount.
											</div>
										)}
									</div>
									<div className="space-y-4">
										{invoicePreview.map((invoice, index) => (
											<div key={invoice.id} className="bg-white p-6 rounded-lg border shadow-sm">
												<div className="flex items-start justify-between">
													<div className="flex items-start gap-4 flex-1">
														<div
															className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold ${getInvoiceTypeInfo(invoice).bgClass} ${getInvoiceTypeInfo(invoice).textClass}`}
														>
															{invoice.sequence}
														</div>

														<div className="flex-1 space-y-3">
															{/* Description field with status badge */}
															{editingInvoiceId === invoice.id ? (
																<div className="space-y-2">
																	<div className="flex items-center gap-2 mb-2">
																		<span
																			className={`px-2 py-1 rounded-full text-xs font-medium ${getInvoiceTypeInfo(invoice).bgClass} ${getInvoiceTypeInfo(invoice).textClass}`}
																		>
																			{getInvoiceTypeInfo(invoice).label}
																		</span>
																	</div>
																	<Textarea
																		value={getInvoiceValue(invoice.id, "description", invoice.description)}
																		onChange={(e) => updateInvoice(invoice.id, "description", e.target.value)}
																		placeholder="Invoice description"
																		className="min-h-[60px]"
																	/>
																</div>
															) : (
																<div>
																	<div className="flex items-center gap-2 mb-2">
																		<span
																			className={`px-2 py-1 rounded-full text-xs font-medium ${getInvoiceTypeInfo(invoice).bgClass} ${getInvoiceTypeInfo(invoice).textClass}`}
																		>
																			{getInvoiceTypeInfo(invoice).label}
																		</span>
																	</div>
																	<div className="text-base font-medium text-gray-900">{getInvoiceValue(invoice.id, "description", invoice.description)}</div>
																</div>
															)}

															{/* Date fields */}
															<div className="grid grid-cols-2 gap-4">
																{editingInvoiceId === invoice.id ? (
																	<>
																		<div>
																			<label htmlFor={`invoice-date-${invoice.id}`} className="text-sm font-medium text-gray-700 block mb-1">
																				Invoice Date
																			</label>
																			<Input
																				id={`invoice-date-${invoice.id}`}
																				type="date"
																				value={getInvoiceValue(invoice.id, "invoiceDate", invoice.invoiceDate)}
																				onChange={(e) => updateInvoice(invoice.id, "invoiceDate", e.target.value)}
																				className="border"
																			/>
																		</div>
																		<div>
																			<label htmlFor={`due-date-${invoice.id}`} className="text-sm font-medium text-gray-700 block mb-1">
																				Due Date
																			</label>
																			<Input
																				id={`due-date-${invoice.id}`}
																				type="date"
																				value={getInvoiceValue(invoice.id, "dueDate", invoice.dueDate)}
																				onChange={(e) => updateInvoice(invoice.id, "dueDate", e.target.value)}
																				className="border"
																			/>
																		</div>
																	</>
																) : (
																	<>
																		<div>
																			<span className="text-sm font-medium text-gray-500">Invoice Date</span>
																			<div className="flex items-center gap-1 text-sm text-gray-900 mt-1">
																				<IconCalendar className="w-4 h-4 text-gray-500" />
																				{new Date(getInvoiceValue(invoice.id, "invoiceDate", invoice.invoiceDate)).toLocaleDateString("en-US", {
																					month: "short",
																					day: "numeric",
																					year: "numeric",
																				})}
																			</div>
																		</div>
																		<div>
																			<span className="text-sm font-medium text-gray-500">Due Date</span>
																			<div className="text-sm text-red-700 mt-1 font-medium">
																				{new Date(getInvoiceValue(invoice.id, "dueDate", invoice.dueDate)).toLocaleDateString("en-US", {
																					month: "short",
																					day: "numeric",
																					year: "numeric",
																				})}
																			</div>
																		</div>
																	</>
																)}
															</div>

															{/* Period display for recurring invoices */}
															{!isOneTimeProject && (
																<div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
																	<span className="font-medium">Service Period:</span>{" "}
																	{new Date(invoice.startDate).toLocaleDateString("en-US", {
																		month: "short",
																		day: "numeric",
																	})}{" "}
																	-{" "}
																	{new Date(invoice.endDate).toLocaleDateString("en-US", {
																		month: "short",
																		day: "numeric",
																		year: "numeric",
																	})}
																</div>
															)}
														</div>
													</div>

													{/* Amount and actions */}
													<div className="flex flex-col items-end gap-3">
														{editingInvoiceId === invoice.id ? (
															<>
																<div className="flex items-center gap-1">
																	<IconCurrencyPeso className="w-5 h-5 text-gray-500" />
																	<Input
																		type="number"
																		step="0.01"
																		min="0"
																		value={getInvoiceValue(invoice.id, "amount", invoice.amount)}
																		onChange={(e) => updateInvoice(invoice.id, "amount", Number.parseFloat(e.target.value) || 0)}
																		className="w-32 text-lg font-semibold"
																	/>
																</div>
																<div className="flex gap-2">
																	<Button
																		type="button"
																		size="sm"
																		variant="outline"
																		onClick={() => saveInvoiceEdit(invoice.id)}
																		className="text-green-600 border-green-300 hover:bg-green-50"
																	>
																		<IconCheck className="w-4 h-4" />
																	</Button>
																	<Button
																		type="button"
																		size="sm"
																		variant="outline"
																		onClick={() => cancelInvoiceEdit(invoice.id)}
																		className="text-red-600 border-red-300 hover:bg-red-50"
																	>
																		<IconX className="w-4 h-4" />
																	</Button>
																</div>
															</>
														) : (
															<>
																<div className="text-right">
																	<div className="flex items-center justify-end gap-1 text-2xl font-bold text-green-700">
																		<IconCurrencyPeso className="w-6 h-6" />
																		{formatNumber(getInvoiceValue(invoice.id, "amount", invoice.amount))}
																	</div>
																	{form.watch("project_type") === "royalty_project" && form.watch("original_monthly_recurring_sales") > 0 && (
																		<div className="text-xs text-gray-500 mt-0.5">Original: ₱{formatNumber(form.watch("original_monthly_recurring_sales"))}</div>
																	)}
																</div>
																<div className="flex gap-2">
																	<Button
																		type="button"
																		size="sm"
																		variant="outline"
																		onClick={() => setEditingInvoiceId(invoice.id)}
																		className="text-blue-600 border-blue-300 hover:bg-blue-50"
																	>
																		<IconEdit className="w-4 h-4" />
																	</Button>
																	<Button
																		type="button"
																		size="sm"
																		variant="outline"
																		onClick={() => {
																			if (confirm("Are you sure you want to delete this invoice?")) {
																				removeInvoice(invoice.id);
																			}
																		}}
																		className="text-red-600 border-red-300 hover:bg-red-50"
																		title="Delete invoice"
																	>
																		<IconTrash className="w-4 h-4" />
																	</Button>
																</div>
															</>
														)}
													</div>
												</div>
											</div>
										))}
									</div>
								</div>
							) : (
								<div className="text-center py-12">
									<IconFileInvoice className="w-16 h-16 text-gray-300 mx-auto mb-4" />
									<h3 className="text-lg font-medium text-gray-500 mb-2">No invoices to preview</h3>
									<p className="text-sm text-gray-400">Complete the contract details on the left to generate invoice preview</p>
								</div>
							)}
						</div>
					</div>
				</form>
			</Form>
		</div>
	);
};
